import {
  type AppError,
  InvalidTokenError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import type * as z from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {
  AdditionalBackgroundMasterResponse,
  AdditionalBackgroundResponse,
  type AdditionalBackgroundsSchema,
  CompanyMasterResponse,
  CountryMasterResponse,
  DeleteDevelopmentExperienceResponse,
  DeleteFollowCompanyResponse,
  type DeleteFolloyCompanyData,
  type DeleteReportData,
  DeleteReportResponse,
  DevelopmentExperienceResponse,
  DevelopmentServiceResponse,
  type DmSetting,
  DmSettingsResponse,
  EducationResponse,
  EntrysheetResponse,
  ForeignExperienceResponse,
  type FormForeignExperiencesSchema,
  GeneralProfilesResponse,
  type GetMeInterestsData,
  GetMeInterestsResponse,
  HashtagListResponse,
  IndustryMasterResponse,
  IntentionMasterResponse,
  InternResponse,
  InternTermMasterResponse,
  type JobHuntingStatusAndOffersFormSchema,
  JobMasterResponse,
  LanguageMasterResponse,
  LanguageResponse,
  type LanguagesSchema,
  LocationMasterResponse,
  type MentalitiesSchema,
  MentalityResponse,
  OffersResponse,
  PersonalityMasterResponse,
  PostAcEmailAuthResponse,
  PostDevelopmentExperienceResponse,
  PostEmailAuthResponse,
  type PostEntrysheetInput,
  PostEntrysheetOutput,
  PostFollowCompanyResponse,
  type PostFolloyCompanyData,
  PrMessagesResponse,
  type PrMessagesSchema,
  PutAdditionalBackgroundsResponse,
  PutDevelopmentExperienceResponse,
  type PutDevelopmentExperienceSchema,
  PutDevelopmentServiceResponse,
  type PutDmSettingsData,
  PutDmSettingsNonDisplayResponse,
  type PutDmSettingsResponse,
  PutEducationsResponse,
  type PutEducationsSchema,
  PutForeignExperiencesResponse,
  type PutForeignExperiencesSchema,
  type PutGeneralProfilesData,
  PutGeneralProfilesResponse,
  PutInternsResponse,
  type PutInternsSchema,
  type PutItSkillResponse,
  type PutItSkillsSchema,
  PutLanguagesResponse,
  type PutLanguagesSchema,
  PutMentalitiesResponse,
  type PutMyInterestsData,
  PutMyInterestsResponse,
  PutOffersResponse,
  type PutOffersSchema,
  PutPrMessagesResponse,
  type PutScoutSettingsData,
  PutScoutSettingsNonDisplayResponse,
  PutWishesResponse,
  type PutWishesSchema,
  ScoutSettingsResponse,
  SelectionReportResponse,
  type WishFormSchema,
  WishResponse,
} from '../entities/mypage/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

export type ResultDMSetting = Result<DmSettingsResponseType, AppError>;
export type ReturnPutScoutSettingValueType = z.infer<
  typeof PutScoutSettingsNonDisplayResponse
>;

export type ReturnScoutSettingValueType = Result<
  ScoutSettingsResponseType,
  AppError
>;

export type PutDmSettingsResponseType = z.infer<typeof PutDmSettingsResponse>;
export type PutDmSettingsNonDisplayResponseType = z.infer<
  typeof PutDmSettingsNonDisplayResponse
>;
export type PutDmSettingsDataType = z.infer<typeof PutDmSettingsData>;
export type DmSettingsResponseType = z.infer<typeof DmSettingsResponse>;
export type DmSettingType = z.infer<typeof DmSetting>;

export type ScoutSettingsResponseType = z.infer<typeof ScoutSettingsResponse>;
export type PutScoutSettingsDataType =
  | z.infer<typeof PutScoutSettingsData>
  | unknown;

export type PostFollowCompanyDataType = z.infer<typeof PostFolloyCompanyData>;
export type PostFollowCompanyResponseType = z.infer<
  typeof PostFollowCompanyResponse
>;

export type PostEntrysheetInputDataType = z.infer<typeof PostEntrysheetInput>;
export type PostEntrysheetOutputDataType = z.infer<typeof PostEntrysheetOutput>;

type Post2EntrysheetReturnValueType<T> = Result<T, AppError>;
export type PostEntrysheetReturnValueType =
  Post2EntrysheetReturnValueType<PostEntrysheetOutputDataType>;

export type PutMyInterestsResponseType = z.infer<typeof PutMyInterestsResponse>;

type ReturnGetMeInterests = Result<GetMeInterestsResponseType, AppError>;

export type GetMeInterestsResponseType = z.infer<typeof GetMeInterestsResponse>;
export type GetMeInterestsDataType = z.infer<typeof GetMeInterestsData>;
export type DeleteFollowCompanyDataType = z.infer<
  typeof DeleteFolloyCompanyData
>;
export type DeleteFollowCompanyResponseType = z.infer<
  typeof DeleteFollowCompanyResponse
>;

export type DeleteReportDataType = z.infer<typeof DeleteReportData> | unknown;
export type DeleteReportResponseType = z.infer<typeof DeleteReportResponse>;

export type PutMyInterestsDataType = z.infer<typeof PutMyInterestsData>;

export type GeneralProfilesReturnValueType = ResultWithOauthToken<
  GeneralProfilesResponseType,
  AppError
>;
export type ResultGetGeneralProfile = ResultWithOauthToken<
  GeneralProfilesResponseType,
  AppError
>;
export type GeneralProfilesResponseType = z.infer<
  typeof GeneralProfilesResponse
>;
export type PutGeneralProfilesDataType = z.infer<typeof PutGeneralProfilesData>;
export type PutGeneralProfilesResponseType = z.infer<
  typeof PutGeneralProfilesResponse
>;

export type PrMessagesReturnValueType = Result<
  PrMessagesResponseType,
  AppError
>;
export type PrMessagesResponseType = z.infer<typeof PrMessagesResponse>;
export type PutPrMessagesDataType = z.infer<typeof PrMessagesSchema>;
export type PutPrMessagesResponseType = z.infer<typeof PutPrMessagesResponse>;

export type LocationMasterReturnValueType = Result<
  LocationMasterResponseType,
  AppError
>;
export type LocationMasterResponseType = z.infer<typeof LocationMasterResponse>;

export type WishReturnValueType = Result<WishResponseType, AppError>;
export type WishResponseType = z.infer<typeof WishResponse>;
export type PutWishesDataType = z.infer<typeof WishFormSchema>;
export type PutWishesRequestDataType = z.infer<typeof PutWishesSchema>;
export type PutWishesResponseType = z.infer<typeof PutWishesResponse>;

export type JobMasterReturnValueType = Result<JobMasterResponseType, AppError>;
export type JobMasterResponseType = z.infer<typeof JobMasterResponse>;

export type IndustryMasterReturnValueType = Result<
  IndustryMasterResponseType,
  AppError
>;
export type IndustryMasterResponseType = z.infer<typeof IndustryMasterResponse>;

export type CompanyMasterReturnValueType = Result<
  CompanyMasterResponseType,
  AppError
>;
export type CompanyMasterResponseType = z.infer<typeof CompanyMasterResponse>;

export type IntentionMasterReturnValueType = Result<
  IntentionMasterResponseType,
  AppError
>;
export type IntentionMasterResponseType = z.infer<
  typeof IntentionMasterResponse
>;

export type PersonalityMasterReturnValueType = Result<
  PersonalityMasterResponseType,
  AppError
>;
export type PersonalityMasterResponseType = z.infer<
  typeof PersonalityMasterResponse
>;

export type MentalityReturnValueType = Result<MentalityResponseType, AppError>;
export type MentalityResponseType = z.infer<typeof MentalityResponse>;
export type PutMentalitiesDataType = z.infer<typeof MentalitiesSchema>;
export type PutMentalitiesResponseType = z.infer<typeof PutMentalitiesResponse>;

export type EducationReturnValueType = Result<EducationResponseType, AppError>;
export type EducationResponseType = z.infer<typeof EducationResponse>;
export type PutEducationsDataType = z.infer<typeof PutEducationsSchema>;
export type PutEducationsResponseType = z.infer<typeof PutEducationsResponse>;

export type AdditionalBackgroundMasterReturnValueType = Result<
  AdditionalBackgroundMasterResponseType,
  AppError
>;
export type AdditionalBackgroundMasterResponseType = z.infer<
  typeof AdditionalBackgroundMasterResponse
>;
export type AdditionalBackgroundReturnValueType = Result<
  AdditionalBackgroundResponseType,
  AppError
>;
export type AdditionalBackgroundResponseType = z.infer<
  typeof AdditionalBackgroundResponse
>;

export type PutAdditionalBackgroundsDataType = z.infer<
  typeof AdditionalBackgroundsSchema
>;
export type PutAdditionalBackgroundsResponseType = z.infer<
  typeof PutAdditionalBackgroundsResponse
>;
export type LanguageMasterReturnValueType = Result<
  LanguageMasterResponseType,
  AppError
>;
export type LanguageMasterResponseType = z.infer<typeof LanguageMasterResponse>;

export type LanguageReturnValueType = Result<LanguageResponseType, AppError>;
export type LanguageResponseType = z.infer<typeof LanguageResponse>;

export type FormLanguagesDataType = z.infer<typeof LanguagesSchema>;
export type PutLanguagesDataType = z.infer<typeof PutLanguagesSchema>;
export type PutLanguagesResponseType = z.infer<typeof PutLanguagesResponse>;

export type DevelopmentExperienceReturnValueType = Result<
  DevelopmentExperienceResponseType,
  AppError
>;
export type DevelopmentExperienceResponseType = z.infer<
  typeof DevelopmentExperienceResponse
>;

export type DevelopmentServiceReturnValueType = Result<
  DevelopmentServiceResponseType,
  AppError
>;
export type DevelopmentServiceResponseType = z.infer<
  typeof DevelopmentServiceResponse
>;

export type PutItSkillsDataType = z.infer<typeof PutItSkillsSchema>;
export type PutItSkillResponseType = z.infer<typeof PutItSkillResponse>;
export type PutDevelopmentExperienceResponseType = z.infer<
  typeof PutDevelopmentExperienceResponse
>;
export type PutDevelopmentServiceResponseType = z.infer<
  typeof PutDevelopmentServiceResponse
>;

export type PutDevelopmentExperiencesType = z.infer<
  typeof PutDevelopmentExperienceSchema
>;

export type HashtagListReturnValueType = Result<
  HashtagListResponseType,
  AppError
>;
export type HashtagListResponseType = z.infer<typeof HashtagListResponse>;

export type InternTermMasterReturnValueType = Result<
  InternTermMasterResponseType,
  AppError
>;
export type InternTermMasterResponseType = z.infer<
  typeof InternTermMasterResponse
>;

export type InternReturnValueType = Result<InternResponseType, AppError>;
export type InternResponseType = z.infer<typeof InternResponse>;

export type PutInternsDataType = z.infer<typeof PutInternsSchema>;
export type PutInternsResponseType = z.infer<typeof PutInternsResponse>;

export type CountryMasterReturnValueType = Result<
  CountryMasterResponseType,
  AppError
>;
export type CountryMasterResponseType = z.infer<typeof CountryMasterResponse>;

export type ForeignExperienceReturnValueType = Result<
  ForeignExperienceResponseType,
  AppError
>;
export type ForeignExperienceResponseType = z.infer<
  typeof ForeignExperienceResponse
>;

export type FormForeignExperiencesDataType = z.infer<
  typeof FormForeignExperiencesSchema
>;
export type PutForeignExperiencesDataType = z.infer<
  typeof PutForeignExperiencesSchema
>;

export type PutForeignExperiencesResponseType = z.infer<
  typeof PutForeignExperiencesResponse
>;

export type OfferReturnValueType = Result<OffersResponseType, AppError>;
export type OffersResponseType = z.infer<typeof OffersResponse>;

export type PutOffersDataType = z.infer<typeof PutOffersSchema>;
export type JobHuntingStatusAndOffersFormType = z.infer<
  typeof JobHuntingStatusAndOffersFormSchema
>;
export type PutOffersResponseType = z.infer<typeof PutOffersResponse>;
export type PostEmailAuthResponseType = z.infer<typeof PostEmailAuthResponse>;
export type PostAcEmailAuthResponseType = z.infer<
  typeof PostAcEmailAuthResponse
>;

export type EntrysheetReturnValueType = Result<
  EntrysheetResponseType,
  AppError
>;
export type EntrysheetResponseType = z.infer<typeof EntrysheetResponse>;

export type SelectionReportReturnValueType = Result<
  SelectionReportResponseType,
  AppError
>;
export type SelectionReportResponseType = z.infer<
  typeof SelectionReportResponse
>;

type PutDmSettingsArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutDmSettingsDataType;
  version?: string;
};

type PutScoutSettingsArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutScoutSettingsDataType;
  version?: string;
};

type PostFollowCompanyArgs = {
  cookies: Partial<Record<string, string>>;
  data: number;
  version?: string;
};

type PostReportsAddArgs = {
  cookies: Partial<Record<string, string>>;
  data: PostEntrysheetInputDataType;
  version?: string;
};

type DeleteFollowCompanyArgs = {
  cookies: Partial<Record<string, string>>;
  data: number;
  version?: string;
};

type DeleteReportArgs = {
  cookies: Partial<Record<string, string>>;
  reportID: number;
  version?: string;
};

type PutMyInterestsArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutMyInterestsDataType;
  version?: string;
};

type GetDmSettingArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type putDmSettingsType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type GetScoutSettingArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type putScoutSettingsType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

export type GetGeneralProfileArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type putGeneralProfilesType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type PutGeneralProfilesArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutGeneralProfilesDataType;
  version?: string;
};

type GetPrMessageArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type putPrMessagesType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type PutPrMessagesArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutPrMessagesDataType;
  version?: string;
};

type GetLocationMainArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetWishArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type PutWishesArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutWishesRequestDataType;
  version?: string;
};

type GetJobMainArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetIndustryMainArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetCompanyMainArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  param?: string;
};

type GetIntentionMainArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetPersonalityMainArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetMentalityArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type PutMentalitiesArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutMentalitiesDataType;
  version?: string;
};

type GetEducationArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type PutEducationsArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutEducationsDataType;
  version?: string;
};

type GetAdditionalBackgroundMasterArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetAdditionalBackgroundArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type PutAdditionalBackgroundsArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutAdditionalBackgroundsDataType;
  version?: string;
};

type GetLanguageMasterArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetDevelopmentExperienceArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetLanguageArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetDevelopmentServiceArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type PutLanguagesArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutLanguagesDataType;
  version?: string;
};

type PutItSkillsArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutItSkillsDataType;
  version?: string;
};

type PutExperiencesArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutDevelopmentExperiencesType[];
  version?: string;
};

type GetHashtagListArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  param?: string;
};

type GetInternTermMasterArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetInternArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type PutInternsArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutInternsDataType;
  version?: string;
};

type GetCountryMasterArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetForeignExperienceArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type PutForeignExperiencesArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutForeignExperiencesDataType;
  version?: string;
};

type GetOfferArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type PutOffersArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutOffersDataType;
  version?: string;
};

type PostEmailAuthArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type PostAcEmailAuthArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetEntrysheetArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetSelectionReportArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  page?: number;
  limit?: number;
};

type PutWishesType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type putMentalitiesType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type PutEducationsType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type putAdditionalBackgroundsType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type PutLanguagesType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type PutItSkillsType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type PutInternsType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type PutForeignExperiencesType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type PutOffersType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type PostEmailAuthType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type PostAcEmailAuthType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

type UseGetDmSettingOptions = {
  config?: UseQueryOptions<ResultDMSetting>;
  version?: string;
};

type UseGetSelectionReportsOptions = {
  config?: UseQueryOptions<SelectionReportReturnValueType>;
  version?: string;
  page?: number;
  limit?: number;
};

type UseGetEntrysheetsOptions = {
  config?: UseQueryOptions<EntrysheetReturnValueType>;
  version?: string;
};

type UseGetScoutSettingOptions = {
  config?: UseQueryOptions<ReturnScoutSettingValueType>;
  version?: string;
};

export type UseGetGeneralProfileOptions = {
  config?: UseQueryOptions<GeneralProfilesReturnValueType>;
  version?: string;
};

type UseGetPrMessageOptions = {
  config?: UseQueryOptions<PrMessagesReturnValueType>;
  version?: string;
};

type UseGetLocationMainOptions = {
  config?: UseQueryOptions<LocationMasterReturnValueType>;
  version?: string;
};

type UseGetWishOptions = {
  config?: UseQueryOptions<WishReturnValueType>;
  version?: string;
};

type UseGetJobMainOptions = {
  config?: UseQueryOptions<JobMasterReturnValueType>;
  version?: string;
};

type UseGetIndustryMainOptions = {
  config?: UseQueryOptions<IndustryMasterReturnValueType>;
  version?: string;
};

type UseGetCompanyMainOptions = {
  config?: UseQueryOptions<CompanyMasterReturnValueType>;
} & Omit<GetCompanyMainArgs, 'cookies'>;

type UseGetIntentionMainOptions = {
  config?: UseQueryOptions<IntentionMasterReturnValueType>;
  version?: string;
};

type UseGetPersonalityMainOptions = {
  config?: UseQueryOptions<PersonalityMasterReturnValueType>;
  version?: string;
};

type UseGetMentalityOptions = {
  config?: UseQueryOptions<MentalityReturnValueType>;
  version?: string;
};

type UseGetEducationOptions = {
  config?: UseQueryOptions<EducationReturnValueType>;
  version?: string;
};

type UseGetAdditionalBackgroundMasterOptions = {
  config?: UseQueryOptions<AdditionalBackgroundMasterReturnValueType>;
  version?: string;
};

type UseGetAdditionalBackgroundOptions = {
  config?: UseQueryOptions<AdditionalBackgroundReturnValueType>;
  version?: string;
};

type UseGetLanguageMasterOptions = {
  config?: UseQueryOptions<LanguageMasterReturnValueType>;
  version?: string;
};

type UseGetLanguageOptions = {
  config?: UseQueryOptions<LanguageReturnValueType>;
  version?: string;
};

type UseGetDevelopmentExperienceOptions = {
  config?: UseQueryOptions<DevelopmentExperienceReturnValueType>;
  version?: string;
};

type UseGetDevelopmentServiceOptions = {
  config?: UseQueryOptions<DevelopmentServiceReturnValueType>;
  version?: string;
};

type UseGetInternTermMasterOptions = {
  config?: UseQueryOptions<InternTermMasterReturnValueType>;
  version?: string;
};

type UseGetInternOptions = {
  config?: UseQueryOptions<InternReturnValueType>;
  version?: string;
};

type UseGetCountryMasterOptions = {
  config?: UseQueryOptions<CountryMasterReturnValueType>;
  version?: string;
};

type UseGetForeignExperienceOptions = {
  config?: UseQueryOptions<ForeignExperienceReturnValueType>;
  version?: string;
};

type UseGetOfferOptions = {
  config?: UseQueryOptions<OfferReturnValueType>;
  version?: string;
};

export const getDmSettings = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetDmSettingArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<DmSettingsResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/service/users/me/dm-settings`;
    return defaultClient.get(path, DmSettingsResponse);
  };

  const result = await sendApiRequest<DmSettingsResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

export const getScoutSettings = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetScoutSettingArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<ScoutSettingsResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/service/users/me/scout-settings`;
    return defaultClient.get(path, ScoutSettingsResponse);
  };

  const result = await sendApiRequest<ScoutSettingsResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

const putDmSettings = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutDmSettingsArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutDmSettingsNonDisplayResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/service/users/me/dm-settings`;
    return defaultClient.put(path, PutDmSettingsNonDisplayResponse, data);
  };

  const result = await sendApiRequest<PutDmSettingsNonDisplayResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

const putScoutSettings = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutScoutSettingsArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<ReturnPutScoutSettingValueType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/service/users/me/scout-settings`;
    return defaultClient.put(path, PutScoutSettingsNonDisplayResponse, data);
  };

  const result = await sendApiRequest<ReturnPutScoutSettingValueType>({
    request,
    cookies,
    version,
  });

  return result;
};

const postFollowCompany = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PostFollowCompanyArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PostFollowCompanyResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/companies/${data}/follow`;
    return defaultClient.post(path, PostFollowCompanyResponse, data);
  };

  const result = await sendApiRequest<PostFollowCompanyResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

const postEntrysheet = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PostReportsAddArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PostEntrysheetOutputDataType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/entrysheets`;
    return defaultClient.post(path, PostEntrysheetOutput, data);
  };

  const result = await sendApiRequest<PostEntrysheetOutputDataType>({
    request,
    cookies,
    version,
  });

  return result;
};

const deleteFollowCompany = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: DeleteFollowCompanyArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<DeleteFollowCompanyResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/companies/${data}/follow`;
    return defaultClient.delete(path, DeleteFollowCompanyResponse);
  };

  const result = await sendApiRequest<DeleteFollowCompanyResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

const deleteReport = async ({
  cookies,
  reportID,
  version = DEFAULT_API_VERSION,
}: DeleteReportArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<DeleteReportResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/selection-reports/${reportID}`;
    return defaultClient.delete(path, DeleteReportResponse);
  };

  const result = await sendApiRequest<DeleteReportResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

const PutMyInterests = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutMyInterestsArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutMyInterestsResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/interests/${data.interestsId}/status`;
    return defaultClient.put(path, PutMyInterestsResponse, {
      status: data.status,
    });
  };

  const result = await sendApiRequest<PutMyInterestsResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

type GetMeInterestsArgs = {
  cookies: Partial<Record<string, string>>;
  page?: number;
  limit?: number;
  version?: string;
};

type GetMeInterestsOptions = {
  config?: UseQueryOptions<ReturnGetMeInterests>;
} & Omit<GetMeInterestsArgs, 'cookies'>;

export const useGetMeInterests = ({
  limit,
  config,
  version,
}: GetMeInterestsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ReturnGetMeInterests>({
    ...config,
    queryKey: ['getMeInterests', limit],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getMeInterests({cookies, limit, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    keepPreviousData: true,
  });
};

export const getMeInterests = async ({
  cookies,
  page = 1,
  limit = 5,
  version = DEFAULT_API_VERSION,
}: GetMeInterestsArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetMeInterestsResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/interests?page=${page}&limit=${limit}`;
    return defaultClient.get(path, GetMeInterestsResponse);
  };

  const result = await sendApiRequest<GetMeInterestsResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

export const getGeneralProfiles = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetGeneralProfileArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GeneralProfilesResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/general`;
    return defaultClient.get(path, GeneralProfilesResponse);
  };

  const result = await sendApiRequest<GeneralProfilesResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

const putGeneralProfiles = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutGeneralProfilesArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutGeneralProfilesResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/general`;
    return defaultClient.put(path, PutGeneralProfilesResponse, data);
  };

  const result = await sendApiRequest<PutGeneralProfilesResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

export const getPrMessages = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetPrMessageArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PrMessagesResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/pr_message`;
    return defaultClient.get(path, PrMessagesResponse);
  };

  const result = await sendApiRequest<PrMessagesResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

const putPrMessages = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutPrMessagesArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutPrMessagesResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/pr_message`;
    return defaultClient.put(path, PutPrMessagesResponse, data);
  };

  const result = await sendApiRequest<PutPrMessagesResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

export const getLocationMaster = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetLocationMainArgs) => {
  const request = async (): Promise<
    Result<LocationMasterResponseType, AppError>
  > => {
    const path = `/${version}/master/locations`;
    return defaultClient.get(path, LocationMasterResponse);
  };

  const result = await sendApiRequest<LocationMasterResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

export const getWish = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetWishArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<WishResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/wish`;
    return defaultClient.get(path, WishResponse);
  };

  const result = await sendApiRequest<WishResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

const putWishes = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutWishesArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutWishesResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/wish`;
    return defaultClient.put(path, PutWishesResponse, data);
  };

  const result = await sendApiRequest<PutWishesResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

export const getJobMaster = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetJobMainArgs) => {
  const request = async (): Promise<
    Result<JobMasterResponseType, AppError>
  > => {
    const path = `/${version}/master/job-types`;
    return defaultClient.get(path, JobMasterResponse);
  };

  const result = await sendApiRequest<JobMasterResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

export const getIndustryMaster = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetIndustryMainArgs) => {
  const request = async (): Promise<
    Result<IndustryMasterResponseType, AppError>
  > => {
    const path = `/${version}/master/industries`;
    return defaultClient.get(path, IndustryMasterResponse);
  };

  const result = await sendApiRequest<IndustryMasterResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

export const getCompanyMaster = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  param,
}: GetCompanyMainArgs) => {
  const request = async (): Promise<
    Result<CompanyMasterResponseType, AppError>
  > => {
    const path = `/${version}/master/grouped-companies?${
      param ? `q=${param}` : ''
    }`;
    return defaultClient.get(path, CompanyMasterResponse);
  };

  const result = await sendApiRequest<CompanyMasterResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });
  return result;
};

export const getIntentionMaster = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetIntentionMainArgs) => {
  const request = async (): Promise<
    Result<IntentionMasterResponseType, AppError>
  > => {
    const path = `/${version}/master/intentions`;
    return defaultClient.get(path, IntentionMasterResponse);
  };

  const result = await sendApiRequest<IntentionMasterResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

export const getPersonalityMaster = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetPersonalityMainArgs) => {
  const request = async (): Promise<
    Result<PersonalityMasterResponseType, AppError>
  > => {
    const path = `/${version}/master/personalities`;
    return defaultClient.get(path, PersonalityMasterResponse);
  };

  const result = await sendApiRequest<PersonalityMasterResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

export const getMentality = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetMentalityArgs) => {
  const request = async (): Promise<
    Result<MentalityResponseType, AppError>
  > => {
    const path = `/${version}/mypage/profile/edit/mentality`;
    return defaultClient.get(path, MentalityResponse);
  };

  const result = await sendApiRequest<MentalityResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });
  return result;
};

export const getEducation = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetEducationArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<EducationResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/education`;
    return defaultClient.get(path, EducationResponse);
  };

  const result = await sendApiRequest<EducationResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

export const getAdditionalBackgroundMaster = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetAdditionalBackgroundMasterArgs) => {
  const request = async (): Promise<
    Result<AdditionalBackgroundMasterResponseType, AppError>
  > => {
    const path = `/${version}/master/additional-backgrounds`;
    return defaultClient.get(path, AdditionalBackgroundMasterResponse);
  };

  const result = await sendApiRequest<AdditionalBackgroundMasterResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

export const getAdditionalBackground = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetAdditionalBackgroundArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<AdditionalBackgroundResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/additional_background`;
    return defaultClient.get(path, AdditionalBackgroundResponse);
  };

  const result = await sendApiRequest<AdditionalBackgroundResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

export const getLanguageMaster = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetLanguageMasterArgs) => {
  const request = async (): Promise<
    Result<LanguageMasterResponseType, AppError>
  > => {
    const path = `/${version}/master/languages`;
    return defaultClient.get(path, LanguageMasterResponse);
  };

  const result = await sendApiRequest<LanguageMasterResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

export const getLanguage = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetLanguageArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<LanguageResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/language`;
    return defaultClient.get(path, LanguageResponse);
  };

  const result = await sendApiRequest<LanguageResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

export const getInternTermMaster = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetInternTermMasterArgs) => {
  const request = async (): Promise<
    Result<InternTermMasterResponseType, AppError>
  > => {
    const path = `/${version}/master/intern-terms`;
    return defaultClient.get(path, InternTermMasterResponse);
  };

  const result = await sendApiRequest<InternTermMasterResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });
  return result;
};

export const getIntern = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetInternArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<InternResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/intern`;
    return defaultClient.get(path, InternResponse);
  };

  const result = await sendApiRequest<InternResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

export const usePutDmSettings = ({
  onSuccessFnc,
  onErrorFnc,
}: putDmSettingsType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutDmSettingsDataType) => {
      const cookies = parseCookies();
      const result = await putDmSettings({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const usePutScoutSettings = ({
  onSuccessFnc,
  onErrorFnc,
}: putScoutSettingsType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutScoutSettingsDataType) => {
      const cookies = parseCookies();
      const result = await putScoutSettings({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const usePostFollowCompany = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(async (data: number) => {
    const cookies = parseCookies();
    const result = await postFollowCompany({cookies, data});
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    return result;
  });
};

export const usePostEntrysheet = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(async (data: PostEntrysheetInputDataType) => {
    const cookies = parseCookies();
    const result = await postEntrysheet({cookies, data});
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    return result;
  });
};

type PutMyInterestsType = {
  onSuccessFnc: () => void;
  onErrorFnc: (error: unknown) => void;
};

export const usePutMyInterests = ({
  onSuccessFnc,
  onErrorFnc,
}: PutMyInterestsType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutMyInterestsDataType) => {
      const cookies = parseCookies();
      const result = await PutMyInterests({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const useDelectFollowCompany = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(async (data: number) => {
    const cookies = parseCookies();
    const result = await deleteFollowCompany({cookies, data});
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    return result;
  });
};

export const useDeleteReport = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(async (reportID: number) => {
    const cookies = parseCookies();
    const result = await deleteReport({cookies, reportID});
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    return result;
  });
};

const putMentalities = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutMentalitiesArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutMentalitiesResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/mentality`;
    return defaultClient.put(path, PutMentalitiesResponse, data);
  };

  const result = await sendApiRequest<PutMentalitiesResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

const putInterns = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutInternsArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutInternsResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/intern`;
    return defaultClient.put(path, PutInternsResponse, data);
  };

  const result = await sendApiRequest<PutInternsResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

const putEducations = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutEducationsArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutEducationsResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/education`;
    return defaultClient.put(path, PutEducationsResponse, data);
  };

  const result = await sendApiRequest<PutEducationsResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

const putAdditionalBackgrounds = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutAdditionalBackgroundsArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutAdditionalBackgroundsResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/additional_background`;
    return defaultClient.put(path, PutAdditionalBackgroundsResponse, data);
  };

  const result = await sendApiRequest<PutAdditionalBackgroundsResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

const putLanguages = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutLanguagesArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutLanguagesResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/language`;
    return defaultClient.put(path, PutLanguagesResponse, data);
  };

  const result = await sendApiRequest<PutLanguagesResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

export const getDevelopmentExperience = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetDevelopmentExperienceArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<DevelopmentExperienceResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/development-experiences`;
    return defaultClient.get(path, DevelopmentExperienceResponse);
  };

  const result = await sendApiRequest<DevelopmentExperienceResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

export const getDevelopmentService = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetDevelopmentServiceArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<DevelopmentServiceResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/development-service`;
    return defaultClient.get(path, DevelopmentServiceResponse);
  };

  const result = await sendApiRequest<DevelopmentServiceResponseType>({
    request,
    cookies,
    version,
  });
  return result;
};

type DeleteDevelopmentExperienceArgs = {
  cookies: Partial<Record<string, string>>;
  experienceId: number;
  version?: string;
};

const deleteDevelopmentExperience = async ({
  cookies,
  experienceId,
  version = DEFAULT_API_VERSION,
}: DeleteDevelopmentExperienceArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<
    Result<z.infer<typeof DeleteDevelopmentExperienceResponse>, AppError>
  > => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/development-experiences/${experienceId}`;
    return defaultClient.delete(path, DeleteDevelopmentExperienceResponse);
  };

  const result = await sendApiRequest<
    z.infer<typeof DeleteDevelopmentExperienceResponse>
  >({
    request,
    cookies,
    version,
  });

  return result;
};

type PostDevelopmentExperienceArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutItSkillsDataType['experiences'][0];
  version?: string;
};

type PutDevelopmentExperienceArgs = PostDevelopmentExperienceArgs & {
  id: number;
};

const putDevelopmentExperience = async ({
  cookies,
  id,
  data,
  version = DEFAULT_API_VERSION,
}: PutDevelopmentExperienceArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<PutDevelopmentExperienceResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/development-experiences/${id}`;
    return defaultClient.put(path, PutDevelopmentExperienceResponse, data);
  };

  const result = await sendApiRequest<PutDevelopmentExperienceResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

const PostDevelopmentExperience = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PostDevelopmentExperienceArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<
    Result<z.infer<typeof PostDevelopmentExperienceResponse>, AppError>
  > => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/development-experiences`;
    return defaultClient.post(path, PostDevelopmentExperienceResponse, data);
  };

  const result = await sendApiRequest<
    z.infer<typeof PostDevelopmentExperienceResponse>
  >({
    request,
    cookies,
    version,
  });

  return result;
};

type PutDevelopmentServiceArgs = {
  cookies: Partial<Record<string, string>>;
  data: PutItSkillsDataType['service'];
  version?: string;
};

const PutDevelopmentService = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutDevelopmentServiceArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/development-service`;
    return defaultClient.put(path, PutDevelopmentServiceResponse, data);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    version,
  });

  return result;
};

const putItSkills = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutItSkillsArgs) => {
  const updatePromises = [];
  // Experiencesの更新
  updatePromises.push(
    ...data.experiences.map(async (experience) => {
      if (experience.id) {
        if (experience.body === '') {
          // 本文がない場合は削除
          const result = await deleteDevelopmentExperience({
            cookies,
            experienceId: experience.id,
            version,
          });
          return result;
        }

        // 更新: 空欄は詰めて保存する
        const result = await putDevelopmentExperience({
          id: experience.id,
          cookies,
          data: {
            ...experience,
          },
          version,
        });
        return result;
      }

      // Idがなければ新規作成
      if (experience.body !== '') {
        // 空欄は詰めて保存する
        const result = await PostDevelopmentExperience({
          cookies,
          data: {
            ...experience,
          },
          version,
        });
        return result;
      }
    }),
  );

  // Development Serviceの更新
  // 空データは削除して保存
  data.service.mySites = data.service.mySites.filter((value) => value !== '');
  const result = await PutDevelopmentService({
    cookies,
    data: data.service,
    version,
  });

  updatePromises.push(result);

  try {
    const updatedResults = await Promise.all(updatePromises);
    return {ok: true, value: JSON.stringify(updatedResults)};
  } catch (error: unknown) {
    return {ok: false, error: error as AppError};
  }
};

const postDevelopmentExperiences = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutExperiencesArgs) => {
  const updatePromises = [];
  // Experiencesの更新
  updatePromises.push(
    ...data.map(async (experience) => {
      // 空欄は詰めて保存する
      const result = await PostDevelopmentExperience({
        cookies,
        data: {
          ...experience,
        },
        version,
      });
      return result;
    }),
  );

  try {
    const updatedResults = await Promise.all(updatePromises);
    return {ok: true, value: JSON.stringify(updatedResults)};
  } catch (error: unknown) {
    return {ok: false, error: error as AppError};
  }
};

export const getHashtagList = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  param,
}: GetHashtagListArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/hashtags?${
      param ? `q=${param}&` : ''
    }categoryID=12&withUsedCountBy=userDevelopmentExperience`;
    return defaultClient.get(path, HashtagListResponse);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    version,
  });

  return result;
};

export const getCountryMaster = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetCountryMasterArgs) => {
  const request = async (): Promise<
    Result<CountryMasterResponseType, AppError>
  > => {
    const path = `/${version}/master/grouped-countries`;
    return defaultClient.get(path, CountryMasterResponse);
  };

  const result = await sendApiRequest<CountryMasterResponseType>({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

export const getForeignExperience = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetForeignExperienceArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<ForeignExperienceResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/foreign_experience`;
    return defaultClient.get(path, ForeignExperienceResponse);
  };

  const result = await sendApiRequest<ForeignExperienceResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

const putForeignExperiences = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutForeignExperiencesArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/foreign_experience`;
    return defaultClient.put(path, PutForeignExperiencesResponse, data);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    version,
  });

  return result;
};

export const getOffers = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetOfferArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<OffersResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/offer`;
    return defaultClient.get(path, OffersResponse);
  };

  const result = await sendApiRequest<OffersResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

const putOffers = async ({
  cookies,
  data,
  version = DEFAULT_API_VERSION,
}: PutOffersArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/edit/offer`;
    return defaultClient.put(path, PutOffersResponse, data);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    version,
  });
  return result;
};

const postEmailAuth = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: PostEmailAuthArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile`;
    return defaultClient.post(path, PostEmailAuthResponse, {});
  };

  const result = await sendApiRequest({
    request,
    cookies,
    version,
  });

  return result;
};

const postAcEmailAuth = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: PostAcEmailAuthArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/mypage/profile/ac-email/resend`;
    return defaultClient.post(path, PostAcEmailAuthResponse, {});
  };

  const result = await sendApiRequest({
    request,
    cookies,
    version,
  });

  return result;
};

export const getEntrysheets = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetEntrysheetArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<EntrysheetResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/users/me/entrysheets`;
    return defaultClient.get(path, EntrysheetResponse);
  };

  const result = await sendApiRequest<EntrysheetResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

export const getSelectionReports = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  page,
  limit,
}: GetSelectionReportArgs) => {
  const request = async (
    accessToken: string,
  ): Promise<Result<SelectionReportResponseType, AppError>> => {
    defaultClient.setToken(accessToken);
    const path = createPathWithQuery(
      `/${version}/service/users/me/selection-reports`,
      {page, limit},
    );
    return defaultClient.get(path, SelectionReportResponse);
  };

  const result = await sendApiRequest<SelectionReportResponseType>({
    request,
    cookies,
    version,
  });

  return result;
};

export const useGetSelectionReports = ({
  config,
  page,
  limit,
}: UseGetSelectionReportsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<SelectionReportReturnValueType>({
    queryKey: ['selectionReports', page, limit],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getSelectionReports({cookies, page, limit});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    ...config,
  });
};

export const useGetEntrysheets = ({config}: UseGetEntrysheetsOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<EntrysheetReturnValueType>({
    queryKey: ['entrysheets'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getEntrysheets({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    ...config,
  });
};

export const useDmSettings = ({config}: UseGetDmSettingOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ResultDMSetting>(['dmSetting'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getDmSettings({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const useScoutSettings = ({config}: UseGetScoutSettingOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<ReturnScoutSettingValueType>(['scoutSetting'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getScoutSettings({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const useGeneralProfiles = ({config}: UseGetGeneralProfileOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GeneralProfilesReturnValueType>(['generalProfile'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getGeneralProfiles({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const usePutGeneralProfiles = ({
  onSuccessFnc,
  onErrorFnc,
}: putGeneralProfilesType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutGeneralProfilesDataType) => {
      const cookies = parseCookies();
      const result = await putGeneralProfiles({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const usePrMessages = ({config}: UseGetPrMessageOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<PrMessagesReturnValueType>(['prMessage'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getPrMessages({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const usePutPrMessages = ({
  onSuccessFnc,
  onErrorFnc,
}: putPrMessagesType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutPrMessagesDataType) => {
      const cookies = parseCookies();
      const result = await putPrMessages({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const useLocationMaster = ({config}: UseGetLocationMainOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<LocationMasterReturnValueType>(
    ['locationMaster'],
    {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getLocationMaster({cookies});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );

  const locationMainData = data?.ok ? data.value.locations : [];
  return {
    locationMasterData: locationMainData,
    isLoading,
  };
};

export const useWishes = ({config}: UseGetWishOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<WishReturnValueType>(['wish'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getWish({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });

  const wishData = data?.ok
    ? data.value
    : {locations: [], jobs: [], industries: [], companies: []};

  return {
    wishData,
    isLoading,
  };
};

export const usePutWishes = ({onSuccessFnc, onErrorFnc}: PutWishesType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutWishesRequestDataType) => {
      const cookies = parseCookies();
      const result = await putWishes({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const useJobMaster = ({config}: UseGetJobMainOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<JobMasterReturnValueType>(['jobMaster'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getJobMaster({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });

  const jobMainData = data?.ok ? data.value.jobTypes : [];
  return {
    jobMasterData: jobMainData,
    isLoading,
  };
};

export const useIndustryMaster = ({config}: UseGetIndustryMainOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<IndustryMasterReturnValueType>(
    ['industryMaster'],
    {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getIndustryMaster({cookies});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );

  const industryMainData = data?.ok ? data.value.industries : [];
  return {
    industryMasterData: industryMainData,
    isLoading,
  };
};

export const useCompanyMaster = ({config, param}: UseGetCompanyMainOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<CompanyMasterReturnValueType>({
    ...config,
    queryKey: ['companyMaster', param],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getCompanyMaster({cookies, param});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });

  const companyMainData = data?.ok ? data.value.groupedCompanies : [];
  return {
    companyMasterData: companyMainData,
    isLoading,
  };
};

export const useIntentionMaster = ({config}: UseGetIntentionMainOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<IntentionMasterReturnValueType>(
    ['intentionMaster'],
    {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getIntentionMaster({cookies});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );

  const intentionMainData = data?.ok ? data.value.intentions : [];
  return {
    intentionMasterData: intentionMainData,
    isLoading,
  };
};

export const usePersonalityMaster = ({
  config,
}: UseGetPersonalityMainOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  const {data} = useQuery<PersonalityMasterReturnValueType>(
    ['personalityMaster'],
    {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getPersonalityMaster({cookies});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );

  const personalityMainData = data?.ok ? data.value.personalities : [];
  return {
    personalityMasterData: personalityMainData,
  };
};

export const useMentalities = ({config}: UseGetMentalityOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<MentalityReturnValueType>(['mentality'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getMentality({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });

  const mentalityData = data?.ok
    ? data.value
    : {userIntention: [], userPersonality: []};
  return {
    mentalityData,
    isLoading,
  };
};

export const usePutMentalities = ({
  onSuccessFnc,
  onErrorFnc,
}: putMentalitiesType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutMentalitiesDataType) => {
      const cookies = parseCookies();
      const result = await putMentalities({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const useEducations = ({config}: UseGetEducationOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<EducationReturnValueType>(['education'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getEducation({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export const usePutEducations = ({
  onSuccessFnc,
  onErrorFnc,
}: PutEducationsType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutEducationsDataType) => {
      const cookies = parseCookies();
      const result = await putEducations({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const useInternTermMaster = ({
  config,
}: UseGetInternTermMasterOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<InternTermMasterReturnValueType>(
    ['internTermMaster'],
    {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getInternTermMaster({cookies});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );

  const internTermMasterData = data?.ok ? data.value.internTerms : [];
  return {
    internTermMasterData,
    isLoading,
  };
};

export const useInterns = ({config}: UseGetInternOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<InternReturnValueType>(['intern'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getIntern({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });

  const internData = data?.ok
    ? data.value.interns
    : [
        {
          company: {companyID: 0, companyName: '', industryID: 0},
          departmentName: '',
          jobType: {id: 0, name: ''},
          termType: {id: 0, name: ''},
        },
      ];
  return {
    internData,
    isLoading,
  };
};

export const usePutInterns = ({onSuccessFnc, onErrorFnc}: PutInternsType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutInternsDataType) => {
      const cookies = parseCookies();
      const result = await putInterns({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const useAdditionalBackgroundMaster = ({
  config,
}: UseGetAdditionalBackgroundMasterOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<AdditionalBackgroundMasterReturnValueType>(
    ['additionalBackgroundMaster'],
    {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getAdditionalBackgroundMaster({cookies});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );

  const additionalBackgroundMasterData = data?.ok
    ? data.value.additionalBackgrounds
    : [];
  return {
    additionalBackgroundMasterData,
    isLoading,
  };
};

export const useAdditionalBackgrounds = ({
  config,
}: UseGetAdditionalBackgroundOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<AdditionalBackgroundReturnValueType>(
    ['additionalBackground'],
    {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getAdditionalBackground({cookies});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );

  const additionalBackgroundData = data?.ok
    ? data.value.additionalBackgrounds
    : [];
  return {
    additionalBackgroundData,
    isLoading,
  };
};

export const usePutAdditionalBackgrounds = ({
  onSuccessFnc,
  onErrorFnc,
}: putAdditionalBackgroundsType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutAdditionalBackgroundsDataType) => {
      const cookies = parseCookies();
      const result = await putAdditionalBackgrounds({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const useLanguageMaster = ({config}: UseGetLanguageMasterOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<LanguageMasterReturnValueType>(
    ['languageMaster'],
    {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getLanguageMaster({cookies});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );

  const languageMasterData = data?.ok ? data.value.languages : [];
  return {
    languageMasterData,
    isLoading,
  };
};

export const useDevelopmentExperiences = ({
  config,
}: UseGetDevelopmentExperienceOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading, refetch} =
    useQuery<DevelopmentExperienceReturnValueType>(['developmentExperience'], {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getDevelopmentExperience({cookies});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    });

  const developmentExperiencesData = data?.ok
    ? data.value.developmentExperiences
    : [];

  return {
    developmentExperiencesData,
    isLoading,
    refetch,
  };
};

export const useLanguages = ({config}: UseGetLanguageOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<LanguageReturnValueType>(['language'], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getLanguage({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });

  const languageData = data?.ok
    ? data.value
    : {
        toeic: 0,
        toeflType: '',
        toeflPoint: 0,
        languageLevel: [
          {
            languageID: 0,
            languageName: '',
            levelID: 0,
            levelName: '',
          },
        ],
      };

  return {
    languageData,
    isLoading,
  };
};

export const useDevelopmentServices = ({
  config,
}: UseGetDevelopmentServiceOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<DevelopmentServiceReturnValueType>(
    ['developmentService'],
    {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getDevelopmentService({cookies});
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );

  const developmentServicesData = data?.ok
    ? data.value.developmentService
    : {
        id: 0,
        githubID: '',
        qiitaID: '',
        atCoderID: '',
        atCoderRating: 0,
        kaggleID: '',
        kaggleRank: '',
        mySites: [''],
        publishLevel: 0,
        hasEngineerInterest: undefined,
        summaryTags: [],
      };

  return {
    developmentServicesData,
    isLoading,
  };
};

export const usePutLanguages = ({
  onSuccessFnc,
  onErrorFnc,
}: PutLanguagesType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutLanguagesDataType) => {
      const cookies = parseCookies();
      const result = await putLanguages({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const usePutItSkills = ({onSuccessFnc, onErrorFnc}: PutItSkillsType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutItSkillsDataType) => {
      const cookies = parseCookies();
      const result = await putItSkills({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const usePostDevelopmentExperiences = ({
  onSuccessFnc,
  onErrorFnc,
}: PutItSkillsType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutItSkillsDataType['experiences']) => {
      const cookies = parseCookies();
      const result = await postDevelopmentExperiences({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const usePutDevelopmentService = () => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(async (data: PutItSkillsDataType['service']) => {
    const cookies = parseCookies();
    const result = await PutDevelopmentService({cookies, data});
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    return result;
  });
};

export const useCountryMaster = ({config}: UseGetCountryMasterOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<CountryMasterReturnValueType>({
    queryKey: ['countryMaster'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getCountryMaster({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    ...config,
  });

  const countryMasterData = data?.ok ? data.value.countries : [];
  return {
    countryMasterData,
    isLoading,
  };
};

export const useForeignExperiences = ({
  config,
}: UseGetForeignExperienceOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<ForeignExperienceReturnValueType>({
    queryKey: ['foreignExperience'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getForeignExperience({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    ...config,
  });

  const foreignExperiencesData = data?.ok
    ? data.value.foreignExperiences
    : [
        {
          countryID: 0,
          countryName: '',
          startYear: 0,
          startMonth: 0,
          endYear: 0,
          endMonth: 0,
        },
      ];

  return {
    foreignExperiencesData,
    isLoading,
  };
};

export const usePutForeignExperiences = ({
  onSuccessFnc,
  onErrorFnc,
}: PutForeignExperiencesType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutForeignExperiencesDataType) => {
      const cookies = parseCookies();
      const result = await putForeignExperiences({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const useOffers = ({config}: UseGetOfferOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  const {data, isLoading} = useQuery<OfferReturnValueType>({
    queryKey: ['offer'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getOffers({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    ...config,
  });

  const offerData = data?.ok
    ? data.value.offers
    : [
        {
          company: {companyID: 0, companyName: '', industryID: 0},
          departmentName: '',
          jobType: {id: 0, name: ''},
          status: 0,
          reason: '',
        },
      ];
  return {
    offerData,
    isLoading,
  };
};

export const usePutOffers = ({onSuccessFnc, onErrorFnc}: PutOffersType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async (data: PutOffersDataType) => {
      const cookies = parseCookies();
      const result = await putOffers({cookies, data});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const usePostEmailAuth = ({
  onSuccessFnc,
  onErrorFnc,
}: PostEmailAuthType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async () => {
      const cookies = parseCookies();
      const result = await postEmailAuth({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};

export const usePostAcEmailAuth = ({
  onSuccessFnc,
  onErrorFnc,
}: PostAcEmailAuthType) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useMutation(
    async () => {
      const cookies = parseCookies();
      const result = await postAcEmailAuth({cookies});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
    {
      onSuccess() {
        onSuccessFnc();
      },
      onError(error) {
        onErrorFnc(error);
      },
    },
  );
};
